<script setup>
import { watch } from "vue";
import { useDraggableModal } from "@/composables/useDraggableModal";

const props = defineProps({
  show: { type: Boolean, default: false },
});

const emit = defineEmits(["close"]);

// 🔹 NOVO: arrastar o modal pelo título (não há cabeçalho próprio aqui).
// Este componente não desmonta entre aberturas (só alterna `show`), então
// recentraliza ao reabrir para a posição não persistir.
const {
  panelRef,
  panelStyle,
  isDragging,
  isDraggable,
  onHandlePointerDown,
  resetPosition,
} = useDraggableModal();

watch(
  () => props.show,
  (val) => {
    if (val) resetPosition();
  }
);

function closeModal() {
  emit("close");
}
</script>

<template>
  <div
    v-if="show"
    class="fixed inset-0 z-[9998] flex items-center justify-center bg-black/20"
    @click.self="closeModal"
  >
    <div
      ref="panelRef"
      :style="panelStyle"
      class="bg-surface rounded-lg p-4 max-w-md w-full shadow-lg max-h-[80vh] overflow-y-auto"
    >
      <h2
        class="text-xl font-semibold mb-3"
        :class="{
 'cursor-move select-none touch-none': isDraggable,
          'cursor-grabbing': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        Sobre os vínculos Pessoa-Grupo
      </h2>

      <p
        class="text-sm leading-relaxed text-gray-800 mb-3"
      >
        Esta tela é responsável por gerenciar os vínculos entre pessoas
        (usuários) e grupos. Ao associar uma pessoa a um grupo, você permite
        que aquele grupo receba permissões de acesso a textos, atividades e
        notas de forma coletiva, sem precisar configurar usuário por usuário.
      </p>

      <ul
        class="list-disc pl-5 text-sm text-gray-700 space-y-1.5 mb-3"
      >
        <li>
          Use o campo de busca para localizar vínculos filtrando por nome do
          usuário, e-mail ou nome do grupo.
        </li>
        <li>
          Clique em
          <strong>Adicionar Pessoa a Grupo</strong> para criar um novo vínculo
          individual entre um usuário e um grupo específico.
        </li>
        <li>
          Na coluna <strong>Ações</strong>, o menu de três pontos permite
          <strong>editar</strong> o vínculo, <strong>visualizar</strong> em
          detalhes (via cartão) ou <strong>apagar</strong> o vínculo, de
          acordo com as permissões de quem está logado.
        </li>
        <li>
          A coluna <strong>Nome do Usuário</strong> mostra quem está sendo
          vinculado; <strong>Email do Usuário</strong> ajuda a evitar
          confusões quando há pessoas com nomes semelhantes.
        </li>
        <li>
          A coluna <strong>Nome do Grupo</strong> indica em qual grupo o
          usuário está inserido. Grupos podem reunir turmas, equipes ou
          qualquer tipo de agrupamento lógico dentro do sistema.
        </li>
        <li>
          As colunas <strong>Criador</strong> e <strong>Atualizador</strong>
          indicam quem criou e quem fez a última alteração do vínculo,
          auxiliando na auditoria interna.
        </li>
        <li>
          O campo <strong>Adicionado Em</strong> registra a data/hora em que a
          associação foi criada, o que também é útil para entender a evolução
          das permissões ao longo do tempo.
        </li>
        <li>
          Marque vários vínculos na primeira coluna para realizar
          <strong>exclusões em lote</strong> usando o menu
          <strong>Ações em lote</strong>.
        </li>
        <li>
          As opções <strong>Adicionar em Lote</strong> e
          <strong>Remover em Lote</strong> utilizam uma
          <em>operação cartesiana</em>: você seleciona um conjunto de pessoas
          e um conjunto de grupos, e o sistema cria ou remove todos os
          vínculos possíveis entre essas listas de uma só vez.
        </li>
        <li>
          Recomenda-se utilizar as operações em lote com cuidado, sempre
          revisando o impacto esperado (quantidade de vínculos criados ou
          removidos) para evitar alterações massivas indesejadas.
        </li>
      </ul>

      <div class="mt-2 text-right">
        <button
          @click="closeModal"
          class="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition"
        >
          Fechar
        </button>
      </div>
    </div>
  </div>
</template>


